import React, { useRef, useState } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import { ReactComponent as CaretDownIcon } from "@common/assets/images/shared-icons/caret-down-icon.svg";
import * as StyledDropdown from "@common/components/Dropdown/Dropdown.styles";
import { InputElementType } from "@common/components/Input/Input";
import { useInfiniteScrollDetector } from "@common/components/ShakeTable/useInfiniteScrollDetector";
import { Spinner } from "@common/components/Spinner/Spinner";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import * as Styled from "@main/pages/Release/views/ReleaseVersionDropdown.styles";

interface ReleaseVersionDropdownProps {
  releaseVersions: string[];
  selectedVersion: string | null;
  searchValue: string;
  onVersionChange: (version: string) => void;
  onSearchChange: (search: string) => void;
  loading: boolean;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

export const ReleaseVersionDropdown = ({
  releaseVersions,
  selectedVersion,
  searchValue,
  onVersionChange,
  onSearchChange,
  loading,
  hasMore,
  isFetchingNextPage,
  onLoadMore,
}: ReleaseVersionDropdownProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { sendEvent } = useAnalytics();
  const containerRef = useRef<HTMLDivElement>(null);

  const { onContainerScroll } = useInfiniteScrollDetector({
    containerRef,
    hasMore,
    isLoading: isFetchingNextPage,
    loadMore: onLoadMore,
  });

  const handleChangeReleaseVersion = (version: string) => {
    onVersionChange(version);
    setIsMenuOpen(false);
    onSearchChange("");
    sendEvent("release_version_selected", { release_version: version });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value);
  };

  const isLoading = loading && !searchValue;
  const isEmpty = !isLoading && !selectedVersion;
  const label = isLoading ? "Loading..." : isEmpty ? "No releases" : `Release ${selectedVersion}`;

  return (
    <Tooltip
      position="bottom"
      text="Select an app version"
    >
      <Styled.ReleaseVersionDropdown
        position="left"
        label={label}
        buttonElement={<CaretDownIcon />}
        isOpened={isMenuOpen}
        setIsOpened={setIsMenuOpen}
        menuPadding={false}
        onMenuHidden={() => onSearchChange("")}
        disabled={isLoading || isEmpty}
      >
        <Styled.ReleaseVersionDropdownSearch
          elementType={InputElementType.INPUT}
          placeholder="Search..."
          value={searchValue}
          onChange={handleSearchChange}
        />

        {isMenuOpen ? (
          <StyledDropdown.DropdownScroll
            cx={Styled.dropdownScroll}
            ref={containerRef}
            onScroll={() => onContainerScroll()}
          >
            {loading ? (
              <div style={{ margin: "1.4rem 0rem" }}>
                <Spinner style={{ width: "2.2rem", height: "2.2rem" }} />
              </div>
            ) : releaseVersions.length === 0 ? (
              <Styled.NoResultsText>{"No results found"}</Styled.NoResultsText>
            ) : (
              releaseVersions.map((release) => (
                <ReleaseVersionDropdownItem
                  key={release}
                  releaseVersion={release}
                  isSelected={selectedVersion === release}
                  changeReleaseVersion={() => handleChangeReleaseVersion(release)}
                />
              ))
            )}
          </StyledDropdown.DropdownScroll>
        ) : null}
      </Styled.ReleaseVersionDropdown>
    </Tooltip>
  );
};

export interface ReleaseVersionItemProps {
  releaseVersion: string;
  isSelected: boolean;
  changeReleaseVersion: (release: string) => void;
}

export const ReleaseVersionDropdownItem = ({
  releaseVersion,
  isSelected,
  changeReleaseVersion,
}: ReleaseVersionItemProps) => {
  return (
    <Styled.ReleasesDropdownItem
      $clicked={isSelected}
      onClick={() => changeReleaseVersion(releaseVersion)}
    >
      <Flex>
        <Styled.Checkmark>
          {isSelected && (
            <Styled.CheckmarkIcon
              width={16}
              height={16}
            />
          )}
        </Styled.Checkmark>
        <p>{releaseVersion}</p>
      </Flex>
    </Styled.ReleasesDropdownItem>
  );
};
